/**
 * boss-system.ts
 *
 * State machines, movement handlers, phase management, and runtime logic for all
 * boss enemies. Centralizes what was spread across enemy-system.ts,
 * enemy-flow.ts, and wave-scene.ts.
 *
 * Dependency rule: this file MUST NOT import from enemy-system.ts to avoid
 * circular dependencies. Use IBossSpawner for spawn callbacks.
 */
import * as Phaser from "phaser"
import { AUDIO, TEX } from "../core/keys"
import { THEME } from "../core/theme"
import { GAME_HEIGHT, GAME_WIDTH, SCENE_TUNING } from "../data/constants"
import {
  type BossPhaseTransitionSpec,
  ENEMY_DEFINITIONS,
  type EnemyBossPhaseSpec,
  type EnemyMovementSpec,
} from "../data/enemies"
import type {
  BossAttackPattern,
  BrainBossState,
  EnemyData,
  EnemyType,
  GameSprite,
  SnakePartState,
} from "../types"
import { transitionBrainBossPhase } from "./enemy-system"
import type { VfxManager } from "./vfx-manager"

const COLOR = THEME.colors
const BOSS_ADD_DIFFICULTY_STEP = 0.05

// Boss Y-clamp matches ENEMY_SPAWN_TUNING.topMargin / bottomMargin in enemy-system.ts
const BOSS_Y_MIN = 50
const BOSS_Y_MAX = GAME_HEIGHT - 40

// ── Exported types ───────────────────────────────────────────────────────────

export type EnemyBossPhaseHandler = (context: {
  sprite: GameSprite
  enemyData: EnemyData
  phase: number
  scene: Phaser.Scene
  spawnBossAdds: (
    enemyType: EnemyType,
    bossSprite: GameSprite,
    count: number,
  ) => void
  getBaseVx: () => number
  spawnBumperBurst: (count: number) => void
}) => void

export interface BossHudPart {
  id: string
  name: string
  hp: number
  maxHp: number
}

export interface BossHudData {
  bossHp: number
  bossMaxHp: number
  bossPartCount: number
  bossType: string
  bossName: string
  isMainBarVisible: boolean
  parts?: BossHudPart[]
}

/** Allows BossSystem to spawn enemies without importing EnemySystem (avoids circular dep). */
export interface IBossSpawner {
  spawnConfiguredEnemy(
    enemyType: EnemyType,
    x: number,
    y: number,
    overrides?: Partial<EnemyData>,
    options?: {
      introScale?: number
      fadeIn?: boolean
      tweenDurationMs?: number
      depth?: number
    },
  ): GameSprite | null
}

// ── Constants ────────────────────────────────────────────────────────────────

export const BOSS_ATTACK_PATTERNS: Record<
  BossAttackPattern,
  {
    projectileCount: number
    spreadDeg: number
    speedMultiplier: number
    damageMultiplier: number
  }
> = {
  opening: {
    projectileCount: 1,
    spreadDeg: 0,
    speedMultiplier: 0.7, // Slowed down from 1.0
    damageMultiplier: 1,
  },
  pressure: {
    projectileCount: 3,
    spreadDeg: 15, // Slightly wider spread to allow gaps
    speedMultiplier: 0.75, // Slowed down from 1.05
    damageMultiplier: 1.05,
  },
  climax: {
    projectileCount: 5,
    spreadDeg: 25,
    speedMultiplier: 0.8, // Slowed down from 1.12
    damageMultiplier: 1.15,
  },
  rage: {
    projectileCount: 7,
    spreadDeg: 35,
    speedMultiplier: 0.85, // Slowed down from 1.2
    damageMultiplier: 1.25,
  },
  spiral: {
    projectileCount: 1,
    spreadDeg: 0,
    speedMultiplier: 0.7, // Slowed down from 1.1
    damageMultiplier: 1.1,
  },
  laser: {
    projectileCount: 0,
    spreadDeg: 0,
    speedMultiplier: 0,
    damageMultiplier: 2.0,
  },
  missiles: {
    projectileCount: 3, // Reduced from 4
    spreadDeg: 30, // Wider spread
    speedMultiplier: 0.6, // Slowed down from 0.8
    damageMultiplier: 1.5,
  },
  "neural-blast": {
    projectileCount: 10, // Reduced from 12
    spreadDeg: 140, // Much wider spread
    speedMultiplier: 0.9, // Slowed down from 1.3
    damageMultiplier: 1.4,
  },
  "humanoid-burst": {
    projectileCount: 5,
    spreadDeg: 45,
    speedMultiplier: 1.0, // Slowed down from 1.4
    damageMultiplier: 1.1,
  },
}

// ── Internal helpers ─────────────────────────────────────────────────────────

function clampBossY(y: number): number {
  return Phaser.Math.Clamp(y, BOSS_Y_MIN, BOSS_Y_MAX)
}

// ── Movement handler factory ─────────────────────────────────────────────────

/**
 * Returns the movement handler for boss-specific movement specs, or null for
 * non-boss specs (which remain in enemy-system.ts).
 */
export function createBossMovementHandler(
  spec: EnemyMovementSpec,
):
  | ((
      sprite: GameSprite,
      time: number,
      delta: number,
      playerY: number,
      playerX: number,
      scrollCompensation: number,
    ) => void)
  | null {
  switch (spec.action) {
    case "serpentine-boss":
      return (sprite, time, _delta, playerY) => {
        const ed = sprite.entityData as EnemyData
        const homeX = ed.homeX ?? sprite.x
        const homeY = ed.homeY ?? sprite.y

        const primaryWave =
          Math.sin((time * Math.PI * 2) / spec.swoopPeriodMs) *
          spec.swoopAmplitude
        const secondaryWave =
          Math.sin(
            (time * Math.PI * 2 * spec.secondaryWaveMultiplier) /
              spec.swoopPeriodMs,
          ) *
          spec.swoopAmplitude *
          spec.secondaryWaveAmplitude
        const driftX =
          Math.sin((time * Math.PI * 2) / spec.driftPeriodMs) *
          spec.driftAmplitudeX
        const playerPull = (playerY - homeY) * spec.trackSpeed * 0.05

        const targetY = clampBossY(
          homeY + primaryWave + secondaryWave + playerPull,
        )
        const targetX = homeX + driftX

        sprite.y += (targetY - sprite.y) * 0.06
        sprite.x += (targetX - sprite.x) * 0.04

        const verticalSpeed = targetY - sprite.y
        sprite.setAngle(verticalSpeed * 0.8)
      }

    case "brain-boss":
      return (sprite, time, _delta, playerY) => {
        const ed = sprite.entityData as EnemyData
        const brainState = ed.supportState as BrainBossState | undefined
        const homeX = ed.homeX ?? sprite.x
        const homeY = ed.homeY ?? sprite.y
        const isExposed = brainState?.brainPhase === "exposed"

        const amplitude = isExposed
          ? spec.exposedAmplitude
          : spec.shieldedAmplitude
        const periodMs = isExposed
          ? spec.exposedPeriodMs
          : spec.shieldedPeriodMs
        const trackMult = isExposed ? spec.trackSpeed * 2.5 : spec.trackSpeed

        const primaryWave =
          Math.sin((time * Math.PI * 2) / periodMs) * amplitude
        const jitter = isExposed
          ? Math.sin((time * Math.PI * 7.3) / periodMs) * amplitude * 0.2
          : 0
        const driftPeriod = isExposed ? 2800 : 4600
        const driftX =
          Math.sin((time * Math.PI * 2) / driftPeriod) *
          spec.driftAmplitudeX *
          (isExposed ? 1.4 : 1)
        const playerPull = (playerY - homeY) * trackMult * 0.04

        const targetY = clampBossY(homeY + primaryWave + jitter + playerPull)
        const targetX = homeX + driftX

        sprite.y += (targetY - sprite.y) * (isExposed ? 0.08 : 0.04)
        sprite.x += (targetX - sprite.x) * (isExposed ? 0.06 : 0.03)

        const tilt = (targetY - sprite.y) * (isExposed ? 1.2 : 0.5)
        sprite.setAngle(Phaser.Math.Clamp(tilt, -8, 8))
      }

    default:
      return null
  }
}

// ── State factories ───────────────────────────────────────────────────────────

export function createBrainBossState(): BrainBossState {
  const brainDef = ENEMY_DEFINITIONS.brain
  return {
    brainPhase: "node-shielded",
    shieldHp: brainDef.hpBase * 0.6,
    shieldMaxHp: brainDef.hpBase * 0.6,
    transitioned: false,
    nodeIds: [],
  }
}

export function createSnakePartState(
  partRole: "head" | "body" | "tail",
): SnakePartState {
  return {
    partRole,
    flightPhase: "enter",
    phaseTimer: 1200,
    flightTime: 0,
    swoopTargetY: GAME_HEIGHT * 0.5,
    fromSplit: false,
  }
}

// ── Boss phase handler factory ────────────────────────────────────────────────

function applyBossPhaseTransition(
  transition: BossPhaseTransitionSpec,
  context: { sprite: GameSprite; enemyData: EnemyData },
): void {
  const { sprite, enemyData } = context

  if (transition.shootCooldownMultiplier !== undefined) {
    const min = transition.minShootCooldown ?? 0
    enemyData.shootCooldown = Math.max(
      min,
      enemyData.shootCooldown * transition.shootCooldownMultiplier,
    )
  }
  if (transition.ballInteraction !== undefined)
    enemyData.ballInteraction = transition.ballInteraction
  if (transition.bossAttackPattern !== undefined)
    enemyData.bossAttackPattern = transition.bossAttackPattern
  if (transition.supportGateActive !== undefined)
    enemyData.supportGateActive = transition.supportGateActive
  if (transition.tintHex) {
    sprite.setTint(
      Phaser.Display.Color.HexStringToColor(transition.tintHex).color,
    )
  }
  if (transition.clearTint) sprite.clearTint()
}

export function createBossPhaseHandler(
  spec?: EnemyBossPhaseSpec,
): EnemyBossPhaseHandler | undefined {
  if (!spec) return undefined

  switch (spec.action) {
    case "wave-threshold-phases":
      return (context) => {
        const { sprite, enemyData, phase } = context

        if (phase === 1 && spec.initialBallInteraction) {
          enemyData.ballInteraction = spec.initialBallInteraction
        }
        if (phase === 1 && !enemyData.bossAttackPattern) {
          enemyData.bossAttackPattern = "opening"
        }

        const bossWave = enemyData.bossWave ?? 5
        const band =
          spec.bands.find((b) => bossWave <= b.maxWave) ??
          spec.bands[spec.bands.length - 1]
        const transition = band?.transitions.find((t) => t.phase === phase)
        if (!transition) return

        applyBossPhaseTransition(transition, { sprite, enemyData })
        for (const add of transition.spawnAdds ?? []) {
          context.spawnBossAdds(add.type, sprite, add.count)
        }
        if (transition.spawnBumperBurstCount) {
          context.spawnBumperBurst(transition.spawnBumperBurstCount)
        }
      }
  }
}

// ── BossSystem class ──────────────────────────────────────────────────────────

interface BossSystemOptions {
  scene: Phaser.Scene
  enemyGroup: Phaser.Physics.Arcade.Group
  vfx: VfxManager
  spawner: IBossSpawner
  playSfx: (key: string, volume?: number) => void
  spawnBumperBurst: (count: number) => void
  getWaveNumber: () => number
  getPhaseHandler: (enemyType: EnemyType) => EnemyBossPhaseHandler | undefined
  onBossDefeated?: () => void
  /** Called when the snake boss splits at 50% HP. Caller is responsible for destroying the sprite. */
  onSnakeSplit?: (snake: GameSprite) => void
}

export class BossSystem {
  private readonly scene: Phaser.Scene
  private readonly options: BossSystemOptions
  private bossPhaseTransitioned = new Set<string>()

  constructor(options: BossSystemOptions) {
    this.scene = options.scene
    this.options = options
  }

  public resetPhaseTracking(): void {
    this.bossPhaseTransitioned = new Set()
  }

  public syncPart(sprite: GameSprite, enemyData: EnemyData): boolean {
    if (!enemyData.isBossPart || !enemyData.parentId) {
      return true
    }

    let parent: GameSprite | null = null
    for (const child of this.options.enemyGroup.children) {
      const candidate = child as GameSprite
      if (candidate.active && candidate.getData("id") === enemyData.parentId) {
        parent = candidate
        break
      }
    }

    if (!parent) {
      sprite.destroy()
      return false
    }

    // Synapse nodes orbit on their own, don't pin them to parent via offset
    if (enemyData.type !== "synapseNode") {
      sprite.x = parent.x + (enemyData.partOffsetX ?? 0)
      sprite.y = parent.y + (enemyData.partOffsetY ?? 0)
      enemyData.x = sprite.x
      enemyData.y = sprite.y
    }

    return true
  }

  /**
   * Per-frame boss tick: initialises boss tracking data, drives snake phase
   * escalation, and cycles the brain boss attack pattern.
   */
  public updateBossState(
    sprite: GameSprite,
    enemyData: EnemyData,
    _time: number,
  ): void {
    // Process spawn requests queued by boss state machines
    if (enemyData.bossSpawnRequest) {
      const req = enemyData.bossSpawnRequest
      enemyData.bossSpawnRequest = undefined
      this.spawnBossAdds(req.type, sprite, req.count, req.parentId)
    }

    const isSnakeBoss =
      enemyData.type === "snake" ||
      enemyData.type === "snakeHead" ||
      enemyData.type === "snakeBody" ||
      enemyData.type === "snakeTail"

    if (!isSnakeBoss && enemyData.type !== "brain") return

    if (enemyData.bossMaxHp === undefined) {
      enemyData.bossMaxHp = enemyData.hp
      enemyData.bossPhase = 1
      enemyData.bossWave = this.options.getWaveNumber()
    }

    if (enemyData.type === "snake") {
      this.updateSnakePhase(sprite, enemyData)
    }
  }

  /**
   * Routes ball damage for the brain boss.
   * During the shielded phase, damage drains the shield instead of HP.
   * Returns true when damage was absorbed (caller should skip normal HP damage).
   */
  public applyBallDamageToBrain(
    enemy: GameSprite,
    damage: number,
    onDefeated: (enemy: GameSprite) => void,
  ): boolean {
    const ed = enemy.entityData
    if (!ed || ed.kind !== "enemy" || ed.type !== "brain") return false

    const brainState = ed.supportState as BrainBossState | undefined
    if (!brainState) return false

    if (brainState.brainPhase === "node-shielded") {
      this.options.vfx.emitImpactBurst(enemy.x, enemy.y, COLOR.info, 0.6)
      return true // Damage fully absorbed while nodes are active
    }

    if (brainState.brainPhase !== "shielded") return false

    brainState.shieldHp -= damage
    this.options.vfx.emitImpactBurst(enemy.x, enemy.y, COLOR.info, 1.0)
    this.options.vfx.showDamageNumber(enemy.x, enemy.y - 18, damage, COLOR.info)

    if (brainState.shieldHp <= 0 && !brainState.transitioned) {
      this.transitionBrainToExposed(enemy, ed, brainState, onDefeated)
    }

    return true
  }

  /**
   * Boss-specific defeat routing.
   * Caller is responsible for VFX (already in defeatEnemy) and enemy.destroy().
   * Returns a descriptor so EnemyFlow.defeatEnemy can decide how to proceed.
   */
  public handleBossDefeat(
    enemy: GameSprite,
    enemyData: EnemyData,
  ): { splitSpawned: boolean; shouldCallBossDefeated: boolean } {
    const isSnakePart =
      enemyData.type === "snakeHead" ||
      enemyData.type === "snakeBody" ||
      enemyData.type === "snakeTail"

    if (enemyData.type === "snake") {
      if (enemyData.snakeHalfSplit) {
        // Already split mid-fight — sprite is being cleaned up, no further spawning needed
        return { splitSpawned: true, shouldCallBossDefeated: false }
      }
      const bossWave = enemyData.bossWave ?? this.options.getWaveNumber()
      const difficulty = 1 + (bossWave - 1) * 0.08
      this.spawnSnakeParts(enemy.x, enemy.y, bossWave, difficulty)
      return { splitSpawned: true, shouldCallBossDefeated: false }
    }

    if (isSnakePart) {
      const allPartsDefeated = this.checkAllSnakePartsDefeated(enemy)
      return { splitSpawned: false, shouldCallBossDefeated: allPartsDefeated }
    }

    const isBossEnemy =
      (enemyData.bossMaxHp !== undefined || enemyData.type === "brain") &&
      !enemyData.isBossPart
    return { splitSpawned: false, shouldCallBossDefeated: isBossEnemy }
  }

  /** Collect current boss HP/maxHp/partCount for HUD sync. */
  public collectHudData(): BossHudData {
    let bossHp = 0
    let bossMaxHp = 0
    let bossPartCount = 0
    let bossType = ""
    let bossName = ""
    let isMainBarVisible = true
    const parts: BossHudPart[] = []

    let snakeHeadHp = 0
    let snakeHeadMaxHp = 0
    let snakeBodyHp = 0
    let snakeBodyMaxHp = 0
    let snakeTailHp = 0
    let snakeTailMaxHp = 0

    let hasFullSnake = false
    let hasSnakeParts = false

    for (const child of this.options.enemyGroup.children) {
      const s = child as GameSprite
      const ed = s.entityData
      if (!s.active || !ed || ed.kind !== "enemy") continue

      // Generic parts collection
      if (ed.isBossPart) {
        parts.push({
          id: ed.id,
          name: ed.type === "synapseNode" ? "SYNAPSE" : ed.type.toUpperCase(),
          hp: ed.hp,
          maxHp: ed.bossMaxHp ?? ed.hp,
        })
        continue // EXPLICIT CONTINUE: Boss parts should NOT be counted in the main HP
      }

      if (ed.type === "snakeHead") {
        hasSnakeParts = true
        snakeHeadHp = ed.hp
        snakeHeadMaxHp = ed.bossMaxHp ?? ed.hp
        bossHp += snakeHeadHp
        bossMaxHp += snakeHeadMaxHp
        bossPartCount++
        parts.push({
          id: ed.id,
          name: "HEAD",
          hp: ed.hp,
          maxHp: ed.bossMaxHp ?? ed.hp,
        })
        continue
      }

      if (ed.type === "snakeBody") {
        hasSnakeParts = true
        snakeBodyHp = ed.hp
        snakeBodyMaxHp = ed.bossMaxHp ?? ed.hp
        bossHp += snakeBodyHp
        bossMaxHp += snakeBodyMaxHp
        bossPartCount++
        parts.push({
          id: ed.id,
          name: "BODY",
          hp: ed.hp,
          maxHp: ed.bossMaxHp ?? ed.hp,
        })
        continue
      }

      if (ed.type === "snakeTail") {
        hasSnakeParts = true
        snakeTailHp = ed.hp
        snakeTailMaxHp = ed.bossMaxHp ?? ed.hp
        bossHp += snakeTailHp
        bossMaxHp += snakeTailMaxHp
        bossPartCount++
        parts.push({
          id: ed.id,
          name: "TAIL",
          hp: ed.hp,
          maxHp: ed.bossMaxHp ?? ed.hp,
        })
        continue
      }

      if (ed.type === "snake" && !ed.snakeHalfSplit) {
        hasFullSnake = true
        bossHp += ed.hp
        bossMaxHp += ed.bossMaxHp ?? ed.hp
        bossPartCount = 1
        bossName = "NEON SERPENT"
        continue
      }

      const isBossRoot =
        (ed.bossMaxHp !== undefined || ed.type === "brain") && !ed.isBossPart
      if (!isBossRoot) continue

      if (ed.type === "brain") {
        const brainState = ed.supportState as BrainBossState | undefined
        bossName = "APEX SYSTEM CORE"

        if (brainState?.brainPhase === "node-shielded") {
          isMainBarVisible = false
        }

        if (
          brainState?.brainPhase === "node-shielded" ||
          brainState?.brainPhase === "shielded"
        ) {
          // In these phases, main HP includes direct brain HP + shield HP
          bossHp += (brainState.shieldHp ?? 0) + ed.hp
          bossMaxHp += (brainState.shieldMaxHp ?? 0) + (ed.bossMaxHp ?? ed.hp)
          bossPartCount++
          bossType = "brain"

          // Add shield as a part if it has HP
          if (brainState.shieldHp > 0) {
            parts.push({
              id: `${ed.id}-shield`,
              name: "CORE SHIELD",
              hp: brainState.shieldHp,
              maxHp: brainState.shieldMaxHp,
            })
          }
          continue
        }
      }

      bossHp += ed.hp
      bossMaxHp += ed.bossMaxHp ?? ed.hp
      bossPartCount++
      bossType = ed.type
      if (!bossName) {
        bossName =
          ed.type === "humanoid" ? "APEX COMMANDER" : ed.type.toUpperCase()
      }
    }

    if (hasFullSnake) {
      bossType = "snake"
    } else if (hasSnakeParts) {
      bossType = "snake-split"
    }

    return {
      bossHp,
      bossMaxHp,
      bossPartCount,
      bossType,
      bossName,
      isMainBarVisible,
      parts,
    }
  }

  public spawnSnakeParts(
    x: number,
    y: number,
    bossWave: number,
    difficultyMultiplier: number,
    hpPerPart?: number,
  ): number {
    const partTypes: EnemyType[] = ["snakeHead", "snakeBody", "snakeTail"]
    const offsets = [
      { ox: -30, oy: -50 },
      { ox: 0, oy: 0 },
      { ox: 30, oy: 50 },
    ]
    let spawnedCount = 0

    for (let i = 0; i < partTypes.length; i++) {
      const partType = partTypes[i]
      const { ox, oy } = offsets[i]
      const def = ENEMY_DEFINITIONS[partType]
      const spawnX = Math.min(GAME_WIDTH + 60, x + ox)
      const spawnY = clampBossY(y + oy)
      const partHp = hpPerPart ?? def.hpBase * difficultyMultiplier

      const partState = createSnakePartState(
        partType === "snakeHead"
          ? "head"
          : partType === "snakeBody"
            ? "body"
            : "tail",
      )
      partState.fromSplit = true

      const sprite = this.options.spawner.spawnConfiguredEnemy(
        partType,
        spawnX,
        spawnY,
        {
          hp: partHp,
          damage: def.damage * difficultyMultiplier,
          points: def.points,
          shootCooldown: def.shootCooldown,
          bossMaxHp: partHp,
          bossWave,
          supportState: partState,
        },
        { introScale: 0.4, fadeIn: true, tweenDurationMs: 600, depth: 80 },
      )
      if (sprite) spawnedCount++
    }

    return spawnedCount
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private getBossAddDifficultyMultiplier(): number {
    const waveNumber = this.options.getWaveNumber()
    return 1 + (waveNumber - 1) * BOSS_ADD_DIFFICULTY_STEP
  }

  private updateSnakePhase(sprite: GameSprite, enemyData: EnemyData): void {
    if (!enemyData.bossMaxHp) return
    if (enemyData.snakeHalfSplit) return

    const hpPct = enemyData.hp / enemyData.bossMaxHp

    // At 50% HP, break into parts instead of a normal phase transition
    if (hpPct <= 0.5) {
      this.triggerHalfHealthSplit(sprite, enemyData)
      return
    }

    let newPhase = 2
    if (hpPct > 0.75) newPhase = 1

    const oldPhase = enemyData.bossPhase ?? 1
    if (newPhase === oldPhase) return

    enemyData.bossPhase = newPhase
    const phaseKey = `${sprite.x}-${sprite.y}-phase${newPhase}`
    if (this.bossPhaseTransitioned.has(phaseKey)) return

    this.bossPhaseTransitioned.add(phaseKey)
    this.options.vfx.shakeCamera(400, 0.025)
    this.options.vfx.flashScreen(COLOR.dangerAlert, 0.3, 200)
    this.options.vfx.emitExplosionBurst(
      sprite.x,
      sprite.y,
      COLOR.dangerAlert,
      1.5,
    )
    this.applyPhaseChanges(sprite, enemyData, newPhase)
  }

  private triggerHalfHealthSplit(
    sprite: GameSprite,
    enemyData: EnemyData,
  ): void {
    enemyData.snakeHalfSplit = true

    const bossWave = enemyData.bossWave ?? this.options.getWaveNumber()
    const difficulty = 1 + (bossWave - 1) * 0.08
    const hpPerPart = Math.max(1, Math.floor(enemyData.hp / 3))

    this.options.vfx.shakeCamera(800, 0.06)
    this.options.vfx.flashScreen(COLOR.dangerAlert, 0.7, 500)
    this.options.vfx.emitExplosionBurst(
      sprite.x,
      sprite.y,
      COLOR.dangerAlert,
      3.0,
    )
    this.options.playSfx(AUDIO.EXPLOSION, 0.9)

    this.spawnSnakeParts(sprite.x, sprite.y, bossWave, difficulty, hpPerPart)

    this.options.onSnakeSplit?.(sprite)
  }

  private applyPhaseChanges(
    sprite: GameSprite,
    enemyData: EnemyData,
    phase: number,
  ): void {
    const phaseHandler = this.options.getPhaseHandler(enemyData.type)
    if (!phaseHandler) return

    phaseHandler({
      sprite,
      enemyData,
      phase,
      scene: this.scene,
      spawnBossAdds: (type, boss, count) =>
        this.spawnBossAdds(type, boss, count),
      getBaseVx: () => ENEMY_DEFINITIONS[enemyData.type].vx,
      spawnBumperBurst: this.options.spawnBumperBurst,
    })
  }

  private spawnBossAdds(
    enemyType: EnemyType,
    bossSprite: GameSprite,
    count: number,
    parentId?: string,
  ): void {
    const def = ENEMY_DEFINITIONS[enemyType]
    const difficulty = this.getBossAddDifficultyMultiplier()

    for (let i = 0; i < count; i++) {
      const offsetY = (i - (count - 1) / 2) * 80
      const spawnX = GAME_WIDTH + SCENE_TUNING.enemySpawnOffsetX + 40 + i * 30
      const spawnY = Phaser.Math.Clamp(
        bossSprite.y + offsetY,
        50,
        GAME_HEIGHT - 50,
      )

      const isBossPart = enemyType === "synapseNode"
      const hp = def.hpBase * difficulty

      this.options.spawner.spawnConfiguredEnemy(
        enemyType,
        spawnX,
        spawnY,
        {
          vx: def.vx,
          vy: def.vy,
          hp: hp,
          damage: def.damage * difficulty,
          points: def.points,
          shootCooldown: def.shootCooldown,
          lastShot: 0,
          x: spawnX,
          y: spawnY,
          parentId: parentId ?? bossSprite.getData("id"),
          isBossPart: isBossPart,
          bossMaxHp: isBossPart ? hp : undefined,
        },
        { introScale: 0.5, fadeIn: true, tweenDurationMs: 300, depth: 90 },
      )
    }
  }

  private checkAllSnakePartsDefeated(excludeSprite: GameSprite): boolean {
    for (const child of this.options.enemyGroup.children) {
      const s = child as GameSprite
      const sed = s.entityData
      if (
        s.active &&
        s !== excludeSprite &&
        sed &&
        sed.kind === "enemy" &&
        (sed.type === "snakeHead" ||
          sed.type === "snakeBody" ||
          sed.type === "snakeTail")
      ) {
        return false
      }
    }
    return true
  }

  private transitionBrainToExposed(
    enemy: GameSprite,
    ed: EnemyData,
    brainState: BrainBossState,
    onDefeated: (enemy: GameSprite) => void,
  ): void {
    transitionBrainBossPhase(enemy, brainState, "exposed")
    brainState.transitioned = true

    this.options.vfx.shakeCamera(800, 0.05)
    this.options.vfx.flashScreen(COLOR.danger, 0.6, 400)
    this.options.vfx.playDebrisExplosion(enemy.x, enemy.y, 2.0)
    this.options.vfx.emitExplosionBurst(enemy.x, enemy.y, COLOR.info, 2.5)
    this.options.playSfx(AUDIO.EXPLOSION, 0.8)

    enemy.setTexture(TEX.BOSS_BRAIN_EXPOSED)
    if (enemy.scene.anims.exists("anim_brain_exposed")) {
      enemy.play("anim_brain_exposed")
    }
    const def = ENEMY_DEFINITIONS.brain
    enemy.setDisplaySize(def.spriteSize.w, def.spriteSize.h)
    ed.bossAttackPattern = "pressure"
    ed.shootCooldown = Math.min(ed.shootCooldown, 1400)
    this.spawnBossAdds("kamikaze", enemy, 2)

    const overflow = Math.abs(brainState.shieldHp)
    if (overflow > 0) {
      ed.lastDamageReceived = overflow
      ed.hp -= overflow
      if (ed.hp <= 0) onDefeated(enemy)
    }
  }
}
